import { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart4, Droplets, Activity } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useCurrentUserCompany } from "@/hooks/useCurrentUserCompany";
import { useData } from "@/hooks/useData";
import { ProductComparisonTable } from "@/components/results/ProductComparisonTable";
import { CarbonResults } from "@/components/results/CarbonResults";
import { HydricResults } from "@/components/results/HydricResults";
import { DistributionAnalysisSection } from "@/components/results/DistributionAnalysisSection";
import { SharingSection } from "@/components/results/SharingSection";
import {
  FootprintSummary,
  FootprintDocument,
  FootprintProduct,
  FootprintConsumption,
  AllocationMethod,
} from "@/types/results";
import { useMemo } from "react";

export const Results = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("carbon");
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<string>("");
  const { company, loading: companyLoading } = useCurrentUserCompany();

  // Fetch data from the new collections
  const { data: footprintDocuments, loading: footprintsLoading } = useData<FootprintDocument>({
    collectionName: "footprints",
  });

  const { data: footprintProducts, loading: productsLoading } = useData<FootprintProduct>({
    collectionName: "footprint_products",
  });

  const { data: footprintConsumptions, loading: consumptionsLoading } = useData<FootprintConsumption>({
    collectionName: "footprint_consumptions",
  });

  // Filter and organize data for current company
  const companyData = useMemo(() => {
    if (companyLoading) {
      return { footprints: [], products: [], consumptions: [] };
    }

    if (!company?.id) {
      return { footprints: [], products: [], consumptions: [] };
    }

    // Filter footprint documents for the current company
    const companyFootprints = footprintDocuments?.filter((fp) => fp.company_id === company.id) || [];

    // Get all footprint IDs for this company
    const companyFootprintIds = companyFootprints.map((fp) => fp.id);

    // Filter products and consumptions that belong to this company's footprints
    const companyProducts = footprintProducts?.filter((fp) => companyFootprintIds.includes(fp.footprint_id)) || [];
    const companyConsumptions =
      footprintConsumptions?.filter((fc) => companyFootprintIds.includes(fc.footprint_id)) || [];

    return {
      footprints: companyFootprints,
      products: companyProducts,
      consumptions: companyConsumptions,
    };
  }, [company?.id, footprintDocuments, footprintProducts, footprintConsumptions, companyLoading]);

  // Create footprint summaries for the data table
  const footprintSummaries: FootprintSummary[] = useMemo(() => {
    if (companyData.footprints.length === 0) return [];

    const summaries: FootprintSummary[] = [];

    companyData.products.forEach((product) => {
      const footprintDoc = companyData.footprints.find((fp) => fp.id === product.footprint_id);

      if (footprintDoc && product.functional_unit) {
        // Determine allocation method for carbon footprint
        const generalData = footprintDoc.general_data;
        let carbonAllocationMethod: AllocationMethod = "economic"; // Default
        if (generalData) {
          const activityType = generalData.activityType;
          if (activityType === "extraction") {
            const pvpCaro = generalData.pvp_caro_ext || 0;
            const pvpBarato = generalData.pvp_barato_ext || 0;
            const rd = pvpBarato > 0 ? pvpCaro / pvpBarato : Infinity;
            carbonAllocationMethod = rd <= 5 ? "physical" : "economic";
          } else if (activityType === "transformation") {
            // Use optional chaining for transformation properties that might not exist in the type
            const pvpCaro = (generalData as any).pvp_caro_trsf || 0;
            const pvpBarato = (generalData as any).pvp_barato_trsf || 0;
            const rd = pvpBarato > 0 ? pvpCaro / pvpBarato : Infinity;
            carbonAllocationMethod = rd <= 5 ? "physical" : "economic";
          }
        }

        summaries.push({
          id: product.id,
          product_name: product.name || "Unknown Product",
          product_type: product.product_type || "outro",
          stone_type_name: product.stone_type_name || "Não especificado",
          year: footprintDoc.year,
          functional_unit: product.functional_unit.quantity || 1,
          unit: product.functional_unit.unit || "m3",
          annual_production: product.annual_production || 0, // Added for total calculations
          carbon_footprint: product.carbon_footprint_total || 0,
          hydric_footprint: product.hydric_footprint_total || 0,
          activity_type: footprintDoc.general_data?.activityType || "extraction",
          allocation_method: carbonAllocationMethod, // Calculated allocation method for carbon
          companyId: footprintDoc.company_id,
        });
      }
    });

    return summaries;
  }, [companyData]);

  // Loading state
  const loading = footprintsLoading || productsLoading || consumptionsLoading || companyLoading;

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-6">{t("results.title")}</h1>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  // Empty state when no company is found
  if (!company) {
    return (
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-6">{t("results.title")}</h1>
        <div className="text-center py-16">
          <Activity className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t("results.empty.noCompany.title")}</h3>
          <p className="text-gray-500 max-w-md mx-auto">{t("results.empty.noCompany.description")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">{t("results.title")}</h1>

      <div className="space-y-6">
        {/* Company Activity Type Header */}
        <Card className="bg-gradient-to-r from-slate-50 to-gray-50 border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Activity className="h-8 w-8 text-slate-600" />
              <div>
                <h2 className="text-lg font-semibold text-slate-800">{company.name}</h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">{t("companyForm.fields.activityType")}:</span>
                  <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                    {company.activityType ? t(`activityTypes.${company.activityType}`) : t("common.notSpecified")}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Product Comparison Table - only show if there's data */}
        {footprintSummaries.length > 0 && <ProductComparisonTable data={footprintSummaries} />}

        {/* Tabbed Chart Analysis Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t("results.detailedAnalysis.title")}</CardTitle>
            <p className="text-sm text-gray-600">
              {t("results.detailedAnalysis.description")}
            </p>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 max-w-md mb-6">
                <TabsTrigger value="carbon" className="flex items-center gap-2">
                  <BarChart4 className="h-4 w-4" />
                  {t("results.tabs.carbon")}
                </TabsTrigger>
                <TabsTrigger value="hydrics" className="flex items-center gap-2">
                  <Droplets className="h-4 w-4" />
                  {t("results.tabs.hydrics")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="carbon">
                <CarbonResults
                  selectedProduct={selectedProduct}
                  selectedYear={selectedYear}
                  onProductChange={setSelectedProduct}
                  onYearChange={setSelectedYear}
                />
              </TabsContent>

              <TabsContent value="hydrics">
                <HydricResults
                  selectedProduct={selectedProduct}
                  selectedYear={selectedYear}
                  onProductChange={setSelectedProduct}
                  onYearChange={setSelectedYear}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Distribution Analysis Section */}
        {footprintSummaries.length > 0 && (
          <DistributionAnalysisSection />
        )}

        {/* Information Sharing Section */}
        {footprintSummaries.length > 0 && selectedProduct && selectedYear && (
          <SharingSection
            companyId={company.id}
            companyName={company.name}
            productId={selectedProduct}
            productName={selectedProduct}
            year={parseInt(selectedYear)}
            footprintType="both"
          />
        )}
      </div>
    </div>
  );
};
